import React from 'react';
import { Box, Typography, Grid, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../context/AuthProvider';
import { useMiscInboxUnreadCount } from '../components/Icons/MiscInboxNotificationBell';
import { StateInformationPortalUrl } from '../Constants';
import { AppRoutes } from '../helper/appRoutes';
import { AppFlags } from '../helper/config';
import {
  ACCENT,
  BORDER,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BG_SUBTLE,
  AdminHomeDashboardTile,
} from '../components/Elements/AdminHomeDashboardTile';

const FolderPlusIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M56 16H32L26 8H8C5.79086 8 4 9.79086 4 12V52C4 54.2091 5.79086 56 8 56H56C58.2091 56 60 54.2091 60 52V20C60 17.7909 58.2091 16 56 16Z" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M32 28V44" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M24 36H40" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DocumentIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M40 8H16C13.7909 8 12 9.79086 12 12V52C12 54.2091 13.7909 56 16 56H48C50.2091 56 52 54.2091 52 52V20L40 8Z" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M40 8V20H52" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 28H44" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 36H44" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M20 44H32" stroke={TEXT_SECONDARY} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const DollarIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 8V56" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M44 20H26C23.8783 20 21.8434 20.8429 20.3431 22.3431C18.8429 23.8434 18 25.8783 18 28C18 30.1217 18.8429 32.1566 20.3431 33.6569C21.8434 35.1571 23.8783 36 26 36H38C40.1217 36 42.1566 36.8429 43.6569 38.3431C45.1571 39.8434 46 41.8783 46 44C46 46.1217 45.1571 48.1566 43.6569 49.6569C42.1566 51.1571 40.1217 52 38 52H18" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CursorIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12L28 52L34 36L50 30L12 12Z" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
    <path d="M34 36L50 52" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const InfoIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#b45309" strokeWidth="2.5" fill="none"/>
    <path d="M32 44V32" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="32" cy="22" r="2" fill="#b45309"/>
  </svg>
);

const ArrowCircleIcon = () => (
  <svg width="40" height="40" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="32" cy="32" r="24" stroke="#b45309" strokeWidth="2.5" fill="none"/>
    <path d="M24 32H40" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M34 26L40 32L34 38" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalculatorIcon = () => (
  <svg width="36" height="36" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="14" y="10" width="36" height="44" rx="4" stroke={ACCENT} strokeWidth="2.5" fill="none"/>
    <path d="M22 20H42M22 28H28M34 28H42M22 36H28M34 36H42M22 44H42" stroke={ACCENT} strokeWidth="2.5" strokeLinecap="round"/>
  </svg>
);

export const AdminHomePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const miscUnreadCount = useMiscInboxUnreadCount();

  const handleFilingQueueClick = () => {
      navigate(`${AppRoutes.root}8`);
  };

  const handleStateReportsClick = () => {
      navigate(AppRoutes.reporting.stateReport);
  };

  const handleReconciliationQueueClick = () => {
    navigate(`${AppRoutes.reconciliationQueue}/0`);
  };

  const handleIWantToClick = () => {
    navigate(AppRoutes.home.iWantTo);
  };

  const handleStateInfoPortalClick = () => {
    window.open(StateInformationPortalUrl, '_blank');
  };

  const handleNotificationsClick = () => {
    navigate(AppRoutes.misc.notifications);
  };

  const handleSurplusLinesTaxEstimatorClick = () => {
    navigate(AppRoutes.home.surplusLinesTaxEstimator);
  };

  return (
    <Box
      sx={{
        minHeight: { xs: 'calc(100vh - 85px)', md: 'calc(100vh - 250px)' },
        backgroundColor: BG_SUBTLE,
        paddingTop: { xs: '40px', md: '84px' },
        marginLeft: { xs: '-24px', md: '0px' },
        marginRight: { xs: '-24px', md: '0px' },
        paddingBottom: 3,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            borderBottom: `1px solid ${BORDER}`,
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            mb: 3,
            px: 3,
            py: 2.5,
            boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
            border: `1px solid ${BORDER}`,
          }}
        >
          <Typography
            sx={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: TEXT_PRIMARY,
              mb: 0.25,
            }}
          >
            Hello, {user?.userFirstName || 'User'}!
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              fontWeight: 400,
              color: TEXT_SECONDARY,
            }}
          >
            Let's get started — choose an option below.
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <AdminHomeDashboardTile
              icon={<FolderPlusIcon />}
              title="Filing Queue"
              onClick={handleFilingQueueClick}
              iconBgColor="#f0f9ff"
            />
          </Grid>
          {AppFlags.enableStateReports &&
            (user?.userRoles?.includes('MISC State Reporter') || user?.userRoles?.includes('Super Admin')) && (
            <Grid item xs={12} sm={6} md={4}>
              <AdminHomeDashboardTile
                icon={<DocumentIcon />}
                title="State Reports"
                onClick={handleStateReportsClick}
                iconBgColor="#f1f5f9"
              />
            </Grid>
          )}
          {(user?.userRoles?.includes('MISC State Reporter') || user?.userRoles?.includes('Super Admin')) && (
            <Grid item xs={12} sm={6} md={4}>
              <AdminHomeDashboardTile
                icon={<DollarIcon />}
                title="Reconciliation Queue"
                onClick={handleReconciliationQueueClick}
                iconBgColor="#f0f9ff"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <AdminHomeDashboardTile
              icon={<CursorIcon />}
              title="I Want To..."
              onClick={handleIWantToClick}
              iconBgColor="#f0f9ff"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AdminHomeDashboardTile
              icon={<InfoIcon />}
              title="Go to State Information Portal"
              onClick={handleStateInfoPortalClick}
              iconBgColor="#fff7ed"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AdminHomeDashboardTile
              icon={<ArrowCircleIcon />}
              title="My Notifications"
              onClick={handleNotificationsClick}
              iconBgColor="#fff7ed"
              badgeCount={miscUnreadCount}
            />
          </Grid>
          {AppFlags.enableSurplusLinesTaxEstimator && (
            <Grid item xs={12} sm={6} md={4}>
              <AdminHomeDashboardTile
                icon={<CalculatorIcon />}
                title="Surplus Lines Tax Estimator"
                onClick={handleSurplusLinesTaxEstimatorClick}
                iconBgColor="#f0f9ff"
              />
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminHomePage;